/**
 * Demo scenario fixtures. 30 realistic Workfront-Fusion scenarios
 * covering the surfaces a salesperson walks a prospect through:
 *
 *  - **Active / inactive mix** (25 active, 5 inactive) so the
 *    "Filter by status" affordance has something to show.
 *  - **Realistic Workfront module names** the prospect will recognise
 *    (Project / Task / Update / Custom Form CTGY refs).
 *  - **A range of scheduled cadences** (every 5 min, hourly, daily,
 *    weekly, on-demand) so the cadence-grouped view shows variety.
 *  - **Recent run counts + error counts** that map cleanly to KPI
 *    fixtures below — the dashboard MainMetric tile pulls from the
 *    same totals.
 *  - **Per-scenario `workfrontObjects` + `screenshotLink`** so the
 *    detail modal's Workfront tab + screenshot panel have content
 *    in demo mode (not just empty placeholders).
 *  - **Multiple teams + folders** so the team-filter dropdown and
 *    folder selector show realistic variety.
 *
 * Field shape mirrors what the engine's `/engine/v1/scenarios` and
 * `/engine/v1/scenarios/{fusionId}` routes return today.
 *
 * Sales-demo intent: this expands the previous 8-scenario sketch to
 * a customer-sized dataset (~30 scenarios is typical for a
 * mid-deployment customer). Operator-reported pre-expansion: "8
 * scenarios is small amount, need ~30, no workfront objects, no
 * screenshots for scenarios".
 */

import { tagsForScenario } from './tags.js';

type Row = Record<string, unknown>;

const DEMO_ORG_ID = '900001';
const TEAM_OPS = { id: '700001', name: 'Operations' };
const TEAM_ENG = { id: '700002', name: 'Engineering' };
const TEAM_CS = { id: '700003', name: 'Customer Success' };
const TEAM_FINANCE = { id: '700004', name: 'Finance' };
const TEAM_HR = { id: '700005', name: 'People & HR' };

/**
 * Inline SVG used for the screenshot panel + presentation slide.
 * URL-encoded (not base64) so unicode glyphs in scenario names survive
 * the round-trip — `btoa` throws on non-Latin1 input and Node + jsdom
 * test envs trip on the same SVGs that the browser would accept.
 * Several variants give visual diversity across rows.
 *
 * The label is escaped for XML (replaces `<`, `>`, `&`, `"`, `'`)
 * before injection — a scenario named `<script>alert(1)</script>`
 * would otherwise become live HTML when the SVG is loaded into an
 * `<img>` tag (data URIs share an origin with the document for
 * scripting purposes — this is the relevant XSS surface).
 */
function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function svgScreenshot(label: string, accent: string): string {
  const safeLabel = escapeXml(label);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 450" width="800" height="450">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#1e293b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
    </defs>
    <rect width="800" height="450" fill="url(#g)"/>
    <g stroke="${accent}" stroke-width="2" fill="none" opacity="0.85">
      <circle cx="160" cy="225" r="40"/>
      <circle cx="320" cy="160" r="32"/>
      <circle cx="320" cy="290" r="32"/>
      <circle cx="480" cy="225" r="36"/>
      <circle cx="640" cy="225" r="40"/>
      <path d="M200 225 L288 160 M200 225 L288 290 M352 160 L444 215 M352 290 L444 235 M516 225 L600 225"/>
    </g>
    <g fill="${accent}" opacity="0.85">
      <text x="160" y="230" text-anchor="middle" font-family="system-ui" font-size="14" fill="white">Trigger</text>
      <text x="320" y="165" text-anchor="middle" font-family="system-ui" font-size="12" fill="white">Match</text>
      <text x="320" y="295" text-anchor="middle" font-family="system-ui" font-size="12" fill="white">Filter</text>
      <text x="480" y="230" text-anchor="middle" font-family="system-ui" font-size="14" fill="white">Transform</text>
      <text x="640" y="230" text-anchor="middle" font-family="system-ui" font-size="14" fill="white">Action</text>
    </g>
    <text x="400" y="50" text-anchor="middle" font-family="system-ui" font-weight="600" font-size="20" fill="#e2e8f0">${safeLabel}</text>
    <text x="400" y="420" text-anchor="middle" font-family="system-ui" font-size="11" fill="#94a3b8">Workfront Fusion scenario flow - demo mode</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Workfront object builder — 3-5 objects per scenario, mixing types
 * (PROJ / TASK / OPTASK / CTGY / DOCU) so the detail-modal Workfront
 * tab has variety to show.
 */
function wfObj(
  id: number,
  workfrontType: string,
  workfrontTypeLabel: string,
  workfrontName: string,
  fusionModuleName: string,
): Row {
  return {
    id,
    workfrontId: `wf-${id}`,
    workfrontType,
    workfrontTypeLabel,
    workfrontLink: `https://example.workfront.com/${workfrontType.toLowerCase()}/${id}`,
    workfrontName,
    fusionModuleId: 1000 + id,
    fusionModuleName,
    fusionModuleType: 'workfront',
  };
}

/**
 * Compact scenario constructor — the explicit-object form below got
 * unwieldy at 30 rows. This helper applies sensible defaults and lets
 * each row override only what's distinct. Active=true / hasNotifications=false
 * are the defaults — explicitly set false / true where the demo needs variety.
 */
interface ScenarioSpec {
  fusionId: string;
  name: string;
  team: { id: string; name: string };
  folder: { id: string; name: string };
  scheduling?: 'indefinitely' | 'on-demand';
  schedulingInterval?: number | null;
  runsCount: number;
  errorsCount: number;
  operationsCount: number;
  transferCount?: number;
  capabilities: string[];
  hasNotifications?: boolean;
  isActive?: boolean;
  isPaused?: boolean;
  modules: Array<{ type: string; name: string }>;
  workfrontObjects?: Row[];
  screenshotAccent?: string;
  description?: string;
  aiTechnicalSummary?: string;
  modifiedBy?: string;
  daysSinceModified?: number;
  status?: 'On' | 'Off';
}

function buildScenario(idx: number, spec: ScenarioSpec): Row {
  const daysSinceModified = spec.daysSinceModified ?? 7;
  const lastUpdated = new Date(Date.now() - 1000 * 60 * 60 * 24 * daysSinceModified).toISOString();
  const scheduledAt =
    spec.scheduling === 'on-demand' ? null : new Date(Date.now() - 1000 * 60 * 10).toISOString();
  const screenshotLink = svgScreenshot(spec.name, spec.screenshotAccent ?? '#fbbf24');
  return {
    id: `demo-scenario-${idx}`,
    fusionId: spec.fusionId,
    name: spec.name,
    description:
      spec.description ??
      `Automation that ${spec.name.toLowerCase()}. Runs on a ${spec.scheduling === 'on-demand' ? 'on-demand' : 'scheduled'} cadence; observes a Workfront trigger, transforms the payload, and writes the result back to the connected systems listed in the modules panel.`,
    status: spec.status ?? (spec.isActive === false ? 'Off' : 'On'),
    isActive: spec.isActive ?? true,
    isPaused: spec.isPaused ?? false,
    fusionOrgId: DEMO_ORG_ID,
    fusionTeamId: spec.team.id,
    teamFusionId: spec.team.id,
    fusionTeamName: spec.team.name,
    teamName: spec.team.name,
    fusionFolderId: spec.folder.id,
    fusionFolderName: spec.folder.name,
    folderFusionId: spec.folder.id,
    folderName: spec.folder.name,
    scheduling: spec.scheduling ?? 'indefinitely',
    schedulingInterval: spec.schedulingInterval ?? 300,
    eventSchedule: {
      type: spec.scheduling ?? 'indefinitely',
      interval: spec.schedulingInterval ?? 300,
    },
    eventType: spec.scheduling === 'on-demand' ? 'immediately' : 'scheduled',
    lastUpdated,
    modifiedAt: lastUpdated,
    modifiedBy: spec.modifiedBy ?? 'demo.user@workfocus.io',
    scheduledAt,
    runsCount: spec.runsCount,
    errorsCount: spec.errorsCount,
    transferCount: spec.transferCount ?? spec.runsCount,
    operationsCount: spec.operationsCount,
    lastSuccessfulRunAt: lastUpdated,
    lastSuccessfulRunOperations: spec.operationsCount,
    lastSuccessfulRunDuration: 1200 + ((idx * 137) % 4000),
    capabilities: spec.capabilities,
    applications: spec.capabilities.map((c) => ({
      name: c,
      label: c.charAt(0).toUpperCase() + c.slice(1),
      type: c,
    })),
    hasNotifications: spec.hasNotifications ?? false,
    workfrontLink: 'https://example.workfront.com',
    url: `https://app.workfrontfusion.com/scenarios/${spec.fusionId}`,
    // Realistic Workfront document version-history URL — what the
    // `workfront-backup` Fargate consumer would store on each
    // scenario row after pushing the blueprint to Workfront. Shape:
    // `{tenant}.my.workfront.com/document/{docId}/allVersions`.
    // Different per scenario so the demo's "Workfront Version
    // Control" link is plausible during a sales walkthrough.
    versionsLink: `https://demo.my.workfront.com/document/demo-doc-${spec.fusionId}/allVersions`,
    screenshotLink,
    totalModules: spec.modules.length,
    modulesSummary: { modules: spec.modules },
    workfrontObjects: spec.workfrontObjects ?? [],
    // Tag set lives in `./tags.ts` keyed by fusionId; populates the
    // presentation list's group-by-tag layout + the scenario-detail
    // modal's Tags box in demo mode.
    tags: tagsForScenario(spec.fusionId),
    aiTechnicalSummary:
      spec.aiTechnicalSummary ??
      `## Steps\n\n1. Trigger fires on \`${spec.modules[0]?.type ?? 'event'}\`.\n2. Filter records by the configured business rule.\n3. Transform the payload to match the downstream contract.\n4. Write to \`${spec.modules[spec.modules.length - 1]?.type ?? 'destination'}\`.\n\n## Notes\n\nGenerated for demo mode; the real summary lands when \`ai-summary-processing\` runs for the cloned tenant.`,
    // Mirrors the new list-response shape: real engine ships
    // `hasAiTechnicalSummary` instead of the full markdown, and the
    // sheet lazy-fetches the text from the detail endpoint. Demo
    // mode keeps both fields so the Sparkles gate + sheet render
    // without any network hop.
    hasAiTechnicalSummary: true,
  };
}

// ─── 30 scenarios ──────────────────────────────────────────────────────

const SPECS: ScenarioSpec[] = [
  {
    fusionId: '101001',
    name: 'Auto-create Project from Intake Form',
    team: TEAM_OPS,
    folder: { id: 'f1', name: 'Project Setup' },
    schedulingInterval: 300,
    runsCount: 4128,
    errorsCount: 12,
    operationsCount: 41280,
    capabilities: ['workfront', 'sharepoint'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchEvents', name: 'Watch Events' },
      { type: 'workfront:CreateProject', name: 'Create Project' },
      { type: 'sharepoint:UploadFile', name: 'Upload File' },
    ],
    workfrontObjects: [
      wfObj(1, 'PROJ', 'Project', 'Q2 Marketing Launch', 'Create Project'),
      wfObj(2, 'TASK', 'Task', 'Kick-off Meeting', 'Create Task'),
      wfObj(3, 'DOCU', 'Document', 'Project Charter', 'Upload File'),
    ],
    screenshotAccent: '#fbbf24',
    daysSinceModified: 2,
  },
  {
    fusionId: '101002',
    name: 'Daily Status Roll-up → Slack',
    team: TEAM_OPS,
    folder: { id: 'f2', name: 'Reporting' },
    schedulingInterval: 86400,
    runsCount: 365,
    errorsCount: 2,
    operationsCount: 2920,
    capabilities: ['workfront', 'slack'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:SearchProjects', name: 'Search Projects' },
      { type: 'slack:CreateMessage', name: 'Create Message' },
    ],
    workfrontObjects: [
      wfObj(10, 'PROJ', 'Project', 'Active Customer Programs', 'Search Projects'),
      wfObj(11, 'PROJ', 'Project', 'Internal Initiatives', 'Search Projects'),
    ],
    screenshotAccent: '#38bdf8',
  },
  {
    fusionId: '101003',
    name: 'Sync Jira Issue → Workfront Task',
    team: TEAM_ENG,
    folder: { id: 'f3', name: 'Cross-System Sync' },
    schedulingInterval: 900,
    runsCount: 1244,
    errorsCount: 38,
    operationsCount: 7464,
    capabilities: ['workfront', 'jira'],
    modules: [
      { type: 'jira:WatchIssues', name: 'Watch Issues' },
      { type: 'workfront:CreateTask', name: 'Create Task' },
      { type: 'workfront:UpdateTask', name: 'Update Task' },
    ],
    workfrontObjects: [
      wfObj(20, 'TASK', 'Task', 'Backlog grooming', 'Create Task'),
      wfObj(21, 'TASK', 'Task', 'Bug — payment-flow timeout', 'Update Task'),
      wfObj(22, 'TASK', 'Task', 'Feature — dark-mode polish', 'Update Task'),
    ],
    screenshotAccent: '#34d399',
    daysSinceModified: 3,
  },
  {
    fusionId: '101004',
    name: 'Weekly Customer Health Score',
    team: TEAM_CS,
    folder: { id: 'f4', name: 'Analytics' },
    schedulingInterval: 604800,
    runsCount: 52,
    errorsCount: 0,
    operationsCount: 14560,
    capabilities: ['salesforce', 'workfront', 'openai'],
    hasNotifications: true,
    modules: [
      { type: 'salesforce:SearchAccounts', name: 'Search Accounts' },
      { type: 'openai:CompletePrompt', name: 'Complete Prompt' },
      { type: 'workfront:UpdateCustomField', name: 'Update Custom Field' },
    ],
    workfrontObjects: [
      wfObj(30, 'PROJ', 'Project', 'Acme Corp — Onboarding', 'Update Custom Field'),
      wfObj(31, 'PROJ', 'Project', 'Globex Industries — Renewal', 'Update Custom Field'),
    ],
    screenshotAccent: '#a78bfa',
    daysSinceModified: 5,
  },
  {
    fusionId: '101005',
    name: 'Document Approval Routing',
    team: TEAM_OPS,
    folder: { id: 'f1', name: 'Project Setup' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    runsCount: 892,
    errorsCount: 5,
    operationsCount: 4460,
    capabilities: ['workfront', 'docusign'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchDocuments', name: 'Watch Documents' },
      { type: 'docusign:CreateEnvelope', name: 'Create Envelope' },
    ],
    workfrontObjects: [
      wfObj(40, 'DOCU', 'Document', 'MSA — Acme Corp', 'Create Envelope'),
      wfObj(41, 'DOCU', 'Document', 'SOW — Globex Phase 2', 'Create Envelope'),
    ],
    screenshotAccent: '#fb923c',
    daysSinceModified: 1,
  },
  {
    fusionId: '101006',
    name: 'Approval Notifications → Teams Channel',
    team: TEAM_ENG,
    folder: { id: 'f3', name: 'Cross-System Sync' },
    schedulingInterval: 600,
    runsCount: 2105,
    errorsCount: 18,
    operationsCount: 8420,
    capabilities: ['workfront', 'msteams'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchApprovals', name: 'Watch Approvals' },
      { type: 'msteams:PostMessage', name: 'Post Message' },
    ],
    workfrontObjects: [
      wfObj(50, 'PROJ', 'Project', 'Capital Budget Approvals', 'Watch Approvals'),
      wfObj(51, 'TASK', 'Task', 'Review — Engineering Capex', 'Watch Approvals'),
    ],
    screenshotAccent: '#22d3ee',
  },
  {
    fusionId: '101007',
    name: '[INACTIVE] Legacy ServiceNow → Workfront',
    team: TEAM_ENG,
    folder: { id: 'f3', name: 'Cross-System Sync' },
    schedulingInterval: 1800,
    isActive: false,
    runsCount: 14920,
    errorsCount: 412,
    operationsCount: 74600,
    capabilities: ['servicenow', 'workfront'],
    modules: [
      { type: 'servicenow:WatchTickets', name: 'Watch Tickets' },
      { type: 'workfront:CreateIssue', name: 'Create Issue' },
    ],
    workfrontObjects: [
      wfObj(60, 'OPTASK', 'Issue', 'INC123456 — Auth outage', 'Create Issue'),
      wfObj(61, 'OPTASK', 'Issue', 'INC123457 — DNS flap', 'Create Issue'),
    ],
    screenshotAccent: '#64748b',
    daysSinceModified: 45,
  },
  {
    fusionId: '101008',
    name: '[INACTIVE] Sandbox Test — Auto-tag Project',
    team: TEAM_OPS,
    folder: { id: 'f5', name: 'Sandbox' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    isActive: false,
    isPaused: true,
    runsCount: 6,
    errorsCount: 1,
    operationsCount: 24,
    capabilities: ['workfront'],
    modules: [{ type: 'workfront:UpdateProject', name: 'Update Project' }],
    workfrontObjects: [wfObj(70, 'PROJ', 'Project', 'Sandbox Test Project', 'Update Project')],
    screenshotAccent: '#94a3b8',
    daysSinceModified: 90,
  },
  // ── 9-15: Operations & approvals heavy ─────────────────────────────
  {
    fusionId: '101009',
    name: 'Auto-assign Tasks by Skill Match',
    team: TEAM_OPS,
    folder: { id: 'f1', name: 'Project Setup' },
    schedulingInterval: 600,
    runsCount: 2842,
    errorsCount: 14,
    operationsCount: 11368,
    capabilities: ['workfront'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchTasks', name: 'Watch Tasks' },
      { type: 'workfront:SearchUsers', name: 'Search Users' },
      { type: 'workfront:AssignTask', name: 'Assign Task' },
    ],
    workfrontObjects: [
      wfObj(80, 'TASK', 'Task', 'Translate landing page → French', 'Assign Task'),
      wfObj(81, 'TASK', 'Task', 'Layout review — Q3 prospectus', 'Assign Task'),
      wfObj(82, 'TASK', 'Task', 'A/B test config — pricing page', 'Assign Task'),
    ],
    screenshotAccent: '#facc15',
    daysSinceModified: 4,
  },
  {
    fusionId: '101010',
    name: 'Resource Allocation Forecast → Tableau',
    team: TEAM_OPS,
    folder: { id: 'f2', name: 'Reporting' },
    schedulingInterval: 86400,
    runsCount: 365,
    errorsCount: 1,
    operationsCount: 7300,
    capabilities: ['workfront', 'tableau'],
    modules: [
      { type: 'workfront:SearchHours', name: 'Search Hours' },
      { type: 'tableau:UpdateDataSource', name: 'Update Data Source' },
    ],
    workfrontObjects: [
      wfObj(90, 'HOURT', 'Hour Type', 'Project hours', 'Search Hours'),
      wfObj(91, 'HOURT', 'Hour Type', 'PTO / unavailable', 'Search Hours'),
    ],
    screenshotAccent: '#0ea5e9',
  },
  {
    fusionId: '101011',
    name: 'Customer Form Submission → Jira Issue',
    team: TEAM_ENG,
    folder: { id: 'f3', name: 'Cross-System Sync' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    runsCount: 678,
    errorsCount: 4,
    operationsCount: 3390,
    capabilities: ['workfront', 'jira'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchCustomFormSubmissions', name: 'Watch Form Submissions' },
      { type: 'jira:CreateIssue', name: 'Create Issue' },
    ],
    workfrontObjects: [
      wfObj(100, 'CTGY', 'Custom Form', 'Customer Bug Report', 'Watch Form Submissions'),
    ],
    screenshotAccent: '#34d399',
  },
  {
    fusionId: '101012',
    name: 'Workfront → Salesforce Opportunity Sync',
    team: TEAM_CS,
    folder: { id: 'f3', name: 'Cross-System Sync' },
    schedulingInterval: 1800,
    runsCount: 1200,
    errorsCount: 22,
    operationsCount: 9600,
    capabilities: ['workfront', 'salesforce'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchProjects', name: 'Watch Projects' },
      { type: 'salesforce:UpsertOpportunity', name: 'Upsert Opportunity' },
    ],
    workfrontObjects: [
      wfObj(110, 'PROJ', 'Project', 'Acme — Phase 3 Sale', 'Upsert Opportunity'),
      wfObj(111, 'PROJ', 'Project', 'Globex — Renewal 2026', 'Upsert Opportunity'),
    ],
    screenshotAccent: '#06b6d4',
    daysSinceModified: 6,
  },
  {
    fusionId: '101013',
    name: 'Time-off Approval → HRIS',
    team: TEAM_HR,
    folder: { id: 'f6', name: 'HR Workflows' },
    schedulingInterval: 600,
    runsCount: 2160,
    errorsCount: 5,
    operationsCount: 8640,
    capabilities: ['workfront', 'bamboohr'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchApprovals', name: 'Watch Approvals' },
      { type: 'bamboohr:CreateTimeOff', name: 'Create Time-Off' },
    ],
    workfrontObjects: [
      wfObj(120, 'ARVPRC', 'Approval Process', 'PTO request workflow', 'Watch Approvals'),
    ],
    screenshotAccent: '#a78bfa',
    daysSinceModified: 2,
  },
  {
    fusionId: '101014',
    name: 'Vendor Invoice → NetSuite',
    team: TEAM_FINANCE,
    folder: { id: 'f7', name: 'Finance' },
    schedulingInterval: 3600,
    runsCount: 8760,
    errorsCount: 47,
    operationsCount: 35040,
    capabilities: ['workfront', 'netsuite'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:WatchDocuments', name: 'Watch Documents' },
      { type: 'netsuite:CreateVendorBill', name: 'Create Vendor Bill' },
    ],
    workfrontObjects: [
      wfObj(130, 'DOCU', 'Document', 'Vendor invoice — AWS Oct', 'Create Vendor Bill'),
      wfObj(131, 'DOCU', 'Document', 'Vendor invoice — Datadog Oct', 'Create Vendor Bill'),
    ],
    screenshotAccent: '#f97316',
  },
  {
    fusionId: '101015',
    name: 'Customer Onboarding Project Template',
    team: TEAM_CS,
    folder: { id: 'f1', name: 'Project Setup' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    runsCount: 240,
    errorsCount: 1,
    operationsCount: 4800,
    capabilities: ['salesforce', 'workfront'],
    hasNotifications: true,
    modules: [
      { type: 'salesforce:WatchOpportunities', name: 'Watch Opportunities' },
      { type: 'workfront:CreateProjectFromTemplate', name: 'Create from Template' },
    ],
    workfrontObjects: [
      wfObj(140, 'TMPL', 'Template', 'Customer Onboarding — Enterprise', 'Create from Template'),
      wfObj(141, 'TMPL', 'Template', 'Customer Onboarding — Mid-market', 'Create from Template'),
    ],
    screenshotAccent: '#fbbf24',
    daysSinceModified: 1,
  },
  // ── 16-22: AI + automation showcase ────────────────────────────────
  {
    fusionId: '101016',
    name: 'AI-Tag Project Description',
    team: TEAM_OPS,
    folder: { id: 'f8', name: 'AI Automations' },
    schedulingInterval: 1800,
    runsCount: 480,
    errorsCount: 2,
    operationsCount: 1920,
    capabilities: ['workfront', 'openai'],
    modules: [
      { type: 'workfront:WatchProjects', name: 'Watch Projects' },
      { type: 'openai:CompletePrompt', name: 'GPT-4 prompt' },
      { type: 'workfront:UpdateProject', name: 'Update Project' },
    ],
    workfrontObjects: [
      wfObj(150, 'PROJ', 'Project', 'Demo: AI tagging — Q4 launch', 'Update Project'),
    ],
    screenshotAccent: '#c084fc',
  },
  {
    fusionId: '101017',
    name: 'Smart Reminder: Stale Tasks',
    team: TEAM_OPS,
    folder: { id: 'f8', name: 'AI Automations' },
    schedulingInterval: 86400,
    runsCount: 365,
    errorsCount: 0,
    operationsCount: 7300,
    capabilities: ['workfront', 'openai', 'msteams'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:SearchTasks', name: 'Search Stale Tasks' },
      { type: 'openai:CompletePrompt', name: 'Draft Reminder' },
      { type: 'msteams:PostMessage', name: 'Send Reminder' },
    ],
    workfrontObjects: [
      wfObj(160, 'TASK', 'Task', 'Update SOC2 docs', 'Search Stale Tasks'),
      wfObj(161, 'TASK', 'Task', 'Quarterly access review', 'Search Stale Tasks'),
    ],
    screenshotAccent: '#a78bfa',
    daysSinceModified: 8,
  },
  {
    fusionId: '101018',
    name: 'AI Risk Score → Custom Field',
    team: TEAM_OPS,
    folder: { id: 'f8', name: 'AI Automations' },
    schedulingInterval: 3600,
    runsCount: 720,
    errorsCount: 3,
    operationsCount: 5760,
    capabilities: ['workfront', 'openai'],
    modules: [
      { type: 'workfront:WatchTasks', name: 'Watch Tasks' },
      { type: 'openai:CompletePrompt', name: 'Risk score (GPT-4)' },
      { type: 'workfront:UpdateCustomField', name: 'Update Custom Field' },
    ],
    workfrontObjects: [
      wfObj(170, 'TASK', 'Task', 'High-risk merge — payments', 'Update Custom Field'),
    ],
    screenshotAccent: '#f472b6',
  },
  {
    fusionId: '101019',
    name: 'AI-Generated Meeting Notes → Project Updates',
    team: TEAM_OPS,
    folder: { id: 'f8', name: 'AI Automations' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    runsCount: 156,
    errorsCount: 0,
    operationsCount: 624,
    capabilities: ['workfront', 'openai', 'zoom'],
    hasNotifications: true,
    modules: [
      { type: 'zoom:WatchRecordings', name: 'Watch Recordings' },
      { type: 'openai:Transcribe', name: 'Whisper Transcribe' },
      { type: 'openai:CompletePrompt', name: 'Summarize' },
      { type: 'workfront:UpdateProject', name: 'Append to Project Update' },
    ],
    workfrontObjects: [
      wfObj(180, 'PROJ', 'Project', 'Q3 Steering Committee', 'Append to Project Update'),
    ],
    screenshotAccent: '#10b981',
    daysSinceModified: 2,
  },
  {
    fusionId: '101020',
    name: 'Smart Project Cloner',
    team: TEAM_OPS,
    folder: { id: 'f1', name: 'Project Setup' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    runsCount: 84,
    errorsCount: 0,
    operationsCount: 2520,
    capabilities: ['workfront'],
    modules: [
      { type: 'workfront:WatchEvents', name: 'Watch Events' },
      { type: 'workfront:CloneProject', name: 'Clone Project' },
      { type: 'workfront:UpdateTask', name: 'Adjust Dates' },
    ],
    workfrontObjects: [wfObj(190, 'PROJ', 'Project', 'Cloned from template', 'Clone Project')],
    screenshotAccent: '#fbbf24',
  },
  {
    fusionId: '101021',
    name: 'Daily Backup of Custom Forms',
    team: TEAM_ENG,
    folder: { id: 'f9', name: 'Maintenance' },
    schedulingInterval: 86400,
    runsCount: 365,
    errorsCount: 0,
    operationsCount: 14600,
    capabilities: ['workfront', 's3'],
    modules: [
      { type: 'workfront:SearchCustomForms', name: 'Search Custom Forms' },
      { type: 'aws-s3:CreateObject', name: 'Upload Backup' },
    ],
    workfrontObjects: [
      wfObj(200, 'CTGY', 'Custom Form', 'Customer Bug Report', 'Search Custom Forms'),
      wfObj(201, 'CTGY', 'Custom Form', 'Project Intake', 'Search Custom Forms'),
      wfObj(202, 'CTGY', 'Custom Form', 'Vendor Onboarding', 'Search Custom Forms'),
    ],
    screenshotAccent: '#64748b',
    daysSinceModified: 1,
  },
  {
    fusionId: '101022',
    name: 'Workfront Backup → GitHub Repo',
    team: TEAM_ENG,
    folder: { id: 'f9', name: 'Maintenance' },
    schedulingInterval: 604800,
    runsCount: 52,
    errorsCount: 0,
    operationsCount: 5200,
    capabilities: ['workfront', 'github'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:Backup', name: 'Backup Tenant' },
      { type: 'github:Push', name: 'Push to repo' },
    ],
    workfrontObjects: [],
    screenshotAccent: '#94a3b8',
  },
  // ── 23-30: Customer-facing + finance flows ─────────────────────────
  {
    fusionId: '101023',
    name: 'Customer NPS Survey → Slack',
    team: TEAM_CS,
    folder: { id: 'f10', name: 'Customer Voice' },
    schedulingInterval: 3600,
    runsCount: 1500,
    errorsCount: 6,
    operationsCount: 6000,
    capabilities: ['typeform', 'workfront', 'slack'],
    hasNotifications: true,
    modules: [
      { type: 'typeform:WatchResponses', name: 'Watch Responses' },
      { type: 'workfront:CreateIssue', name: 'Create Issue' },
      { type: 'slack:CreateMessage', name: 'Post to #cx-alerts' },
    ],
    workfrontObjects: [
      wfObj(230, 'OPTASK', 'Issue', 'NPS — Detractor follow-up: Acme', 'Create Issue'),
    ],
    screenshotAccent: '#38bdf8',
  },
  {
    fusionId: '101024',
    name: 'Renewal Risk → CSM Outreach',
    team: TEAM_CS,
    folder: { id: 'f10', name: 'Customer Voice' },
    schedulingInterval: 86400,
    runsCount: 365,
    errorsCount: 2,
    operationsCount: 5475,
    capabilities: ['salesforce', 'workfront', 'gainsight'],
    hasNotifications: true,
    modules: [
      { type: 'gainsight:WatchHealthScore', name: 'Watch Health Score' },
      { type: 'workfront:CreateTask', name: 'CSM Outreach Task' },
    ],
    workfrontObjects: [
      wfObj(240, 'TASK', 'Task', 'CSM call — Globex renewal', 'CSM Outreach Task'),
      wfObj(241, 'TASK', 'Task', 'CSM call — Initech renewal', 'CSM Outreach Task'),
    ],
    screenshotAccent: '#f43f5e',
    daysSinceModified: 3,
  },
  {
    fusionId: '101025',
    name: 'Monthly Invoice → DocuSign',
    team: TEAM_FINANCE,
    folder: { id: 'f7', name: 'Finance' },
    schedulingInterval: 2592000,
    runsCount: 12,
    errorsCount: 0,
    operationsCount: 7200,
    capabilities: ['workfront', 'docusign', 'netsuite'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:GenerateInvoice', name: 'Generate Invoice' },
      { type: 'docusign:CreateEnvelope', name: 'Send for Signature' },
      { type: 'netsuite:CreateInvoice', name: 'Sync to NetSuite' },
    ],
    workfrontObjects: [
      wfObj(250, 'PROJ', 'Project', 'Acme — Monthly retainer Oct', 'Generate Invoice'),
    ],
    screenshotAccent: '#fbbf24',
  },
  {
    fusionId: '101026',
    name: '[INACTIVE] Old Salesforce → Workfront Project',
    team: TEAM_CS,
    folder: { id: 'f5', name: 'Sandbox' },
    schedulingInterval: 1800,
    isActive: false,
    runsCount: 5800,
    errorsCount: 240,
    operationsCount: 29000,
    capabilities: ['salesforce', 'workfront'],
    modules: [
      { type: 'salesforce:WatchOpportunities', name: 'Watch Opportunities' },
      { type: 'workfront:CreateProject', name: 'Create Project' },
    ],
    workfrontObjects: [],
    screenshotAccent: '#64748b',
    daysSinceModified: 120,
  },
  {
    fusionId: '101027',
    name: 'Employee Onboarding Workflow',
    team: TEAM_HR,
    folder: { id: 'f6', name: 'HR Workflows' },
    scheduling: 'on-demand',
    schedulingInterval: null,
    runsCount: 48,
    errorsCount: 0,
    operationsCount: 1440,
    capabilities: ['bamboohr', 'workfront', 'okta'],
    hasNotifications: true,
    modules: [
      { type: 'bamboohr:WatchNewHires', name: 'Watch New Hires' },
      { type: 'workfront:CreateProjectFromTemplate', name: 'Onboarding template' },
      { type: 'okta:CreateUser', name: 'Provision SSO' },
    ],
    workfrontObjects: [
      wfObj(270, 'TMPL', 'Template', 'New Hire — Engineering', 'Onboarding template'),
      wfObj(271, 'TMPL', 'Template', 'New Hire — Sales', 'Onboarding template'),
    ],
    screenshotAccent: '#a78bfa',
    daysSinceModified: 4,
  },
  {
    fusionId: '101028',
    name: 'Project Cost → Power BI',
    team: TEAM_FINANCE,
    folder: { id: 'f2', name: 'Reporting' },
    schedulingInterval: 21600,
    runsCount: 1460,
    errorsCount: 4,
    operationsCount: 11680,
    capabilities: ['workfront', 'powerbi'],
    modules: [
      { type: 'workfront:SearchProjects', name: 'Search Projects' },
      { type: 'workfront:GetCostData', name: 'Get Cost Data' },
      { type: 'powerbi:UpdateDataset', name: 'Update Dataset' },
    ],
    workfrontObjects: [
      wfObj(280, 'PROJ', 'Project', 'Multi-year — Globex Migration', 'Get Cost Data'),
    ],
    screenshotAccent: '#0ea5e9',
  },
  {
    fusionId: '101029',
    name: 'Daily Standup Bot',
    team: TEAM_ENG,
    folder: { id: 'f10', name: 'Customer Voice' },
    schedulingInterval: 86400,
    runsCount: 365,
    errorsCount: 1,
    operationsCount: 1825,
    capabilities: ['workfront', 'slack'],
    hasNotifications: true,
    modules: [
      { type: 'workfront:SearchTasks', name: 'Search Active Tasks' },
      { type: 'slack:CreateMessage', name: 'Post to #standup' },
    ],
    workfrontObjects: [],
    screenshotAccent: '#22d3ee',
    daysSinceModified: 1,
  },
  {
    fusionId: '101030',
    name: '[INACTIVE] Old Confluence Sync',
    team: TEAM_ENG,
    folder: { id: 'f9', name: 'Maintenance' },
    schedulingInterval: 86400,
    isActive: false,
    runsCount: 280,
    errorsCount: 28,
    operationsCount: 2800,
    capabilities: ['workfront', 'confluence'],
    modules: [
      { type: 'workfront:WatchDocuments', name: 'Watch Documents' },
      { type: 'confluence:CreatePage', name: 'Create Page' },
    ],
    workfrontObjects: [],
    screenshotAccent: '#64748b',
    daysSinceModified: 60,
  },
];

export const DEMO_SCENARIOS: ReadonlyArray<Row> = SPECS.map((spec, i) =>
  buildScenario(i + 1, spec),
);

/**
 * Engine list endpoints return `{ data, total, page, pageSize }`.
 */
export const DEMO_SCENARIOS_PAGED: {
  data: Row[];
  total: number;
  page: number;
  pageSize: number;
} = {
  data: [...DEMO_SCENARIOS],
  total: DEMO_SCENARIOS.length,
  page: 1,
  pageSize: 200,
};

/**
 * Demo scenario detail — keyed by fusionId. The wizard's detail
 * modal pulls a single scenario via `useScenarioDetail(fusionId)`;
 * the demo branch looks up the same record from this map. Falls
 * back to the first scenario when the key isn't recognised (so
 * deep-linking still works even with arbitrary URLs).
 */
export function getDemoScenarioDetail(fusionId: string | undefined): Row | undefined {
  if (!fusionId) return undefined;
  return DEMO_SCENARIOS.find((s) => s.fusionId === fusionId) ?? DEMO_SCENARIOS[0];
}
